import React from "react";
import { Badge } from "reactstrap";
import { TableTitle, UserAvatar } from "../../../../components/Component";
import Icon from "../../../../components/icon/Icon";
import { apiService } from "../../../../services/api";
import { APIServicePath } from "../../../../services/api/api-service-impl";
import { toPageInfo } from "../../../_common/DataConverter";
import moment from "moment";
import { findUpper, toCapitalize } from "../../../../utils/Functions";

export const nmcTableColumns = (onViewDocument) => {
  return [
    {
      name: <TableTitle title={"Ref ID"} />,
      id: "refId",
      selector: (row) => row?.refId ?? "",
      sortable: false,
    },
    {
      name: <TableTitle title={"Name"} />,
      id: "name",
      selector: (row) => row?.name ?? "",
      sortable: true,
      cell: (row) => (
        <UserAvatar
          size="sm"
          name={row?.name}
          image={row?.avatar}
          imageAlt={row?.name}
          text={row?.initials}
          subName={row?.role}
          className="my-2"
        />
      ),
    },
    {
      name: <TableTitle title={"NMC No"} />,
      id: "nmcNo",
      selector: (row) => row?.nmcNo ?? "",
      sortable: true,
      cell: (row) => (
        <span className="text-primary cursor-pointer" onClick={row?.onViewNmc}>
          {row?.nmcNo}
        </span>
      ),
    },
    {
      name: <TableTitle title={"NMC Status"} />,
      id: "nmcStatus",
      selector: (row) => row?.nmcStatus ?? "",
      sortable: false,
      cell: (row) => (
        <Badge color={row?.nmcStatusColor ?? "success"} className="text-uppercase">
          {row?.nmcStatus}
        </Badge>
      ),
    },
    {
      name: <TableTitle title={"NMC Expiry"} />,
      id: "nmcExpiry",
      selector: (row) => row?.nmcExpiry ?? "",
      sortable: true,
    },
    {
      name: <TableTitle title={"Last NMC Check"} />,
      id: "lastNmcCheck",
      selector: (row) => row?.lastNmcCheck ?? "",
      sortable: true,
    },
    {
      name: <TableTitle title={""} align="center" />,
      right: true,
      width: "60px",
      cell: (row) => (
        <div
          className="cursor-pointer d-flex justify-content-start w-100 align-items-center h-100 text-primary"
          onClick={() => onViewDocument && onViewDocument(row)}
          title={row?.pdfUrl ? "View PDF" : "View Screenshot"}
        >
          <Icon name={row?.pdfUrl ? "file" : "eye"} />
        </div>
      ),
    },
  ];
};

//***************************API's*********************************/
export function listNmcNisccCheckAPI(params, callback) {
  apiService
    .postCall(APIServicePath.carerNmcNisccCheckList, params)
    .then((data) => {
      let list = toNmcNisccCheckList(data?.list);
      let pageInfo = toPageInfo(data?.pageInfo);
      callback({ list, pageInfo, latestCheckTimestamp: data?.latestCheckTimestamp }, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function fetchCheckDatesAPI(params, callback) {
  apiService
    .postCall(APIServicePath.nmcNisccCheckDates, params)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function exportNmcNisccCheckAPI(params, callback) {
  apiService
    .postCall(APIServicePath.exportNmcNisccCheck, params, 5 * 60 * 1000)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function runScraperAPI(params, callback) {
  apiService
    .postCall(APIServicePath.carerRunScraper, params)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

//***************************Converters*********************************/
function toNmcNisccCheckList(data) {
  let list = [];
  data?.forEach((element) => {
    list.push(toNmcNisccCheckItem(element));
  });
  return list;
}

function toNmcNisccCheckItem(data) {
  const carer = data?.carer || {};
  const firstName = toCapitalize(carer?.firstName ?? "");
  const lastName = toCapitalize(carer?.lastName ?? "");
  const fullName = data?.name || `${firstName} ${lastName}`.trim();
  const initials = findUpper(firstName.charAt(0)) + findUpper(lastName.charAt(0));

  // Determine status color based on status value
  const getStatusColor = (status) => {
    if (!status) return "secondary";
    const statusUpper = status.toUpperCase();
    if (statusUpper.includes("ACTIVE") || statusUpper.includes("REGISTERED")) return "success";
    if (statusUpper.includes("EXPIRED")) return "danger";
    if (statusUpper.includes("INCORRECT") || statusUpper.includes("NOT FOUND")) return "secondary";
    if (statusUpper.includes("CONDITION")) return "warning";
    return "secondary";
  };

  // Format date from API response
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD-MM-YYYY");
  };
  // Format date and time for last check
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD-MM-YYYY HH:mm");
  };

  const pinType = data?.pinType || "";
  const isNmc = pinType === "NMC";

  return {
    id: data?.id,
    carerId: data?.carerId,
    refId: carer?.refNo ?? "",
    name: fullName,
    initials: initials || "NA",
    role: carer?.carerType?.name ?? "",
    avatar: carer?.carerImage?.imageUrl,
    colourHex: carer?.colourHex,
    // NMC fields
    nmcNo: isNmc ? (data?.nisccNmcPin ?? "") : "",
    nmcStatus: isNmc ? (data?.status ?? "") : "",
    nmcStatusColor: isNmc ? getStatusColor(data?.status) : "secondary",
    nmcExpiry: isNmc ? formatDate(data?.nmcExpiry) : "",
    lastNmcCheck: isNmc ? formatDateTime(data?.lastNisccNmcCheck) : "",
    screenshotUrl: data?.nisccNmcSs ?? "",
    pdfUrl: data?.nmcPdf ?? "",
  };
}
